import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faList } from '@fortawesome/free-solid-svg-icons';
import {
  fetchSystemCaregivers,
  buildSystemCaregiverCacheKey,
  hydrateSystemCaregiverList,
  deleteSystemCaregiver,
  setSorting
} from '../../../store/slices/systemCaregiverSlice';
import { toast } from 'react-toastify';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import './SystemCaregiverManagement.css';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';
import { buildDetailAddressLines, resolveStateDisplay } from '../../../utils/addressDisplay';
import { formatDisplayError } from '../../../utils/passwordValidation';
import { computeTotalPages } from '../../../utils/paginationUtils';
import { appendFromDashboardQuery } from '../../../utils/practiceNavigation';
import { canCreateSystemCaregiver, canEditSystemCaregiver, canDeleteSystemCaregiver } from '../../../utils/roleUtils';
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import EventLogSection from '../../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../../hooks/useEventLogModalScrollLock';
import ReassignmentModal from '../../../components/Common/ReassignmentModal/ReassignmentModal';
import {
  batchReassignPatientsToSystemCaregiver,
  buildEmptyReassignments,
  buildReassignmentsForAll,
  fetchActiveSystemCaregiversForReassignment,
  fetchPatientsForSystemCaregiver,
  getSystemCaregiverPatientName,
  hasUnassignedReassignments
} from '../../../utils/staffReassignmentFlow';
import { AddSystemCaregiverModal } from '../../SystemCaregiverDashboard/AddSystemCaregiver/AddSystemCaregiverModal';
import { STAFF_LIST_TABLE_CARD, staffCol } from '../PracticeDashboard/staffListTableLayout';

const normalizeCaregiverStatusKey = (status) => {
  const v = String(status ?? '').trim().toLowerCase();
  if (v === 'active' || v === 'a' || v === '2') return 'active';
  if (v === 'inactive' || v === 'i' || v === 'i/a' || v === 'in/a' || v === '1') return 'inactive';
  return v;
};

const scStatus = (s) => (normalizeCaregiverStatusKey(s) === 'active' ? 'Active' : 'Inactive');

const getDisplayName = (caregiver) =>
  `${caregiver?.last_name || ''}, ${caregiver?.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() ||
  caregiver?.name ||
  'N/A';

const SystemCaregiverManagement = ({ practiceId, isReadOnly = false, onOpenEventLogs = null, hideEventLogsButton = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearBreadcrumbs, updateBreadcrumbs } = useBreadcrumb();
  const {
    systemCaregivers,
    systemCaregiverFetchedKeys,
    listFetchLoading,
    error,
    pagination,
    sortBy,
    sortOrder
  } = useSelector((state) => state.systemCaregiver);
  const { user } = useSelector((state) => state.auth);
  const allowCreate = !isReadOnly && canCreateSystemCaregiver(user);
  const allowEdit = !isReadOnly && canEditSystemCaregiver(user);
  const allowDelete = !isReadOnly && canDeleteSystemCaregiver(user);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [filteredSystemCaregivers, setFilteredSystemCaregivers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState('10');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caregiverToDelete, setCaregiverToDelete] = useState(null);
  const [showDeleteReassignmentModal, setShowDeleteReassignmentModal] = useState(false);
  const [deleteAssignedPatients, setDeleteAssignedPatients] = useState([]);
  const [deleteAlternativeCaregivers, setDeleteAlternativeCaregivers] = useState([]);
  const [deleteReassignments, setDeleteReassignments] = useState({});
  const [isDeleteReassigning, setIsDeleteReassigning] = useState(false);
  const [isDeleteProcessing, setIsDeleteProcessing] = useState(false);
  const [optimisticDeletedIds, setOptimisticDeletedIds] = useState([]);
  const [localStatusFilter, setLocalStatusFilter] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [caregiverToEdit, setCaregiverToEdit] = useState(null);
  const [isViewModal, setIsViewModal] = useState(false);
  const [showLogSection, setShowLogSection] = useState(false);
  const logOverlayRef = React.useRef(null);
  const logContainerRef = React.useRef(null);
  useEventLogModalScrollLock(showLogSection, logOverlayRef, logContainerRef);

  useEffect(() => {
    if (!practiceId) {
      updateBreadcrumbs([{ label: 'System Caregivers', path: '/system-caregiver-dashboard', active: true }]);
      return () => clearBreadcrumbs();
    }
  }, [updateBreadcrumbs, clearBreadcrumbs, practiceId]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [localSearchTerm, searchTerm]);

  const listParams = useMemo(
    () => ({
      page: currentPage,
      limit: entriesPerPage === 'all' ? 9999 : parseInt(entriesPerPage, 10),
      search: searchTerm,
      status: localStatusFilter || undefined,
      practiceId: practiceId || undefined,
      sortBy,
      sortOrder
    }),
    [currentPage, entriesPerPage, searchTerm, localStatusFilter, practiceId, sortBy, sortOrder]
  );

  const cacheKey = useMemo(() => buildSystemCaregiverCacheKey(listParams), [listParams]);

  useEffect(() => {
    if (!location.state?.refreshSystemCaregiverList) return;
    dispatch(fetchSystemCaregivers({ ...listParams, force: true }));
    navigate(
      {
        pathname: location.pathname,
        search: location.search
      },
      { replace: true, state: {} }
    );
  }, [dispatch, listParams, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (systemCaregiverFetchedKeys[cacheKey]) {
      dispatch(hydrateSystemCaregiverList(cacheKey));
    } else {
      dispatch(fetchSystemCaregivers(listParams));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listParams encoded in cacheKey
  }, [cacheKey, dispatch]);

  useEffect(() => {
    if (!Array.isArray(systemCaregivers)) {
      setFilteredSystemCaregivers([]);
      return;
    }

    let filtered = systemCaregivers.filter((c) => !c.deleted_at);

    if (localSearchTerm.trim()) {
        const qParts = localSearchTerm.toLowerCase().trim().split(/\s+/);
        filtered = filtered.filter((caregiver) => {
          const fullName = getDisplayName(caregiver).toLowerCase();
          const email = (caregiver.email || '').toLowerCase();
          const phone = (caregiver.phone || '').toLowerCase();
          
          return qParts.every(part =>
            fullName.includes(part) ||
            email.includes(part) ||
            phone.includes(part)
          );
        });
      }
      filtered = [...filtered].sort((a, b) => {
        if (sortBy === 'idx') {
          const ai = Number(a.id) || 0;
          const bi = Number(b.id) || 0;
          return sortOrder === 'asc' ? ai - bi : bi - ai;
        }
        if (sortBy === 'status') {
          const o = { active: 1, inactive: 2 };
          const orderA = o[normalizeCaregiverStatusKey(a.status)] || 99;
          const orderB = o[normalizeCaregiverStatusKey(b.status)] || 99;
          
          if (orderA !== orderB) {
            return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
          }
          // Tie-breaker: Name A-Z (always asc unless status sort itself is desc, but usually we want names A-Z)
          const na = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
          const nb = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
          return na.localeCompare(nb);
        }
        
        if (sortBy === 'name' || sortBy === 'first_name') {
        if (sortBy === 'addr') {
          const va = String(a.address?.address_line_1 || a.address_line_1 || '').toLowerCase();
          const vb = String(b.address?.address_line_1 || b.address_line_1 || '').toLowerCase();
          return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        }
        if (sortBy === 'phone') {
          const va = String(a.phone || '').toLowerCase();
          const vb = String(b.phone || '').toLowerCase();
          return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        }
        if (sortBy === 'cell') {
          const va = String(a.cell_phone || '').toLowerCase();
          const vb = String(b.cell_phone || '').toLowerCase();
          return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        }
        if (sortBy === 'fax') {
          const va = String(a.fax || '').toLowerCase();
          const vb = String(b.fax || '').toLowerCase();
          return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        }
        if (sortBy === 'email') {
          const va = String(a.email || '').toLowerCase();
          const vb = String(b.email || '').toLowerCase();
          return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        }
          const na = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
          const nb = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
          return sortOrder === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na);
        }
        
        const va = a[sortBy] || '';
        const vb = b[sortBy] || '';
        return sortOrder === 'asc'
          ? String(va).localeCompare(String(vb), undefined, { numeric: true })
          : String(vb).localeCompare(String(va), undefined, { numeric: true });
      });
      // Remove optimistic deletes (normalize ids — API may return number, UI may pass string)
      filtered = filtered.filter(
        (c) => !optimisticDeletedIds.some((id) => String(id) === String(c.id))
      );

      setFilteredSystemCaregivers(filtered);
  }, [systemCaregivers, sortBy, sortOrder, localSearchTerm, optimisticDeletedIds]);

  const totalPages = computeTotalPages(pagination?.total, entriesPerPage);
  const currentSystemCaregivers = filteredSystemCaregivers;

  const getSafeValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return value;
  };

  const formatAddressLines = (caregiver) =>
    buildDetailAddressLines({
      line1: caregiver.address?.address_line_1 ?? caregiver.address_line_1,
      line2: caregiver.address?.address_line_2 ?? caregiver.address_line_2,
      city: caregiver.address?.city ?? caregiver.city,
      stateDisplay: resolveStateDisplay(caregiver),
      zip: caregiver.address?.zip_code ?? caregiver.zip_code
    });


  const getUsername = (entity) =>
    entity?.username || entity?.user?.username || entity?.login_username || 'N/A';

  const getTwoWayAuth = (entity) => {
    const status =
      entity?.is_two_way_auth ??
      entity?.two_way_authentication_enabled ??
      entity?.twoWayAuth ??
      entity?.two_way_auth ??
      entity?.two_way_authentication ??
      entity?.two_way_authentication_status;

    if (status === 1 || status === true || status === 'Enabled') return 'Enabled';
    if (status === 0 || status === false || status === 'Disabled') return 'Disabled';

    return 'N/A';
  };

  const handleViewSystemCaregiver = useCallback((caregiver) => {
    if (!caregiver) return;
    setCaregiverToEdit(caregiver);
    setIsViewModal(true);
    setShowAddModal(true);
  }, []);

  const handleEditSystemCaregiver = useCallback((caregiverOrId) => {
    let caregiver = null;
    if (caregiverOrId && typeof caregiverOrId === 'object') {
      caregiver = caregiverOrId;
    } else if (caregiverOrId != null) {
      caregiver = systemCaregivers.find((c) => String(c.id) === String(caregiverOrId));
    }
    if (caregiver) {
      setCaregiverToEdit(caregiver);
      setIsViewModal(false);
      setShowAddModal(true);
    }
  }, [systemCaregivers]);

  const handleDeleteSystemCaregiver = useCallback((caregiver) => {
    setCaregiverToDelete(caregiver);
    setShowDeleteModal(true);
  }, []);

  const performSystemCaregiverDelete = async (idToDelete, caregiverName) => {
    setShowDeleteModal(false);
    setShowDeleteReassignmentModal(false);
    setCaregiverToDelete(null);
    setDeleteAssignedPatients([]);
    setDeleteAlternativeCaregivers([]);
    setDeleteReassignments({});
    setOptimisticDeletedIds((prev) => [...prev, idToDelete]);
    toast.success(`System Caregiver ${caregiverName} deleted successfully`);

    try {
      await dispatch(deleteSystemCaregiver(idToDelete)).unwrap();
      dispatch(fetchSystemCaregivers({ ...listParams, force: true }));
    } catch (err) {
      setOptimisticDeletedIds((prev) => prev.filter((id) => id !== idToDelete));
      console.error('Failed to delete system caregiver:', err);
      toast.error('Failed to delete system caregiver');
      dispatch(fetchSystemCaregivers({ ...listParams, force: true }));
    }
  };

  const startSystemCaregiverDeleteFlow = async () => {
    if (!caregiverToDelete) return;

    const idToDelete = caregiverToDelete.id;
    const caregiverName = getDisplayName(caregiverToDelete);
    setShowDeleteModal(false);
    setIsDeleteProcessing(true);

    try {
      const patients = await fetchPatientsForSystemCaregiver(idToDelete);
      const alternatives = await fetchActiveSystemCaregiversForReassignment(idToDelete, {
        practiceId,
        patients
      });

      setDeleteAssignedPatients(patients);
      setDeleteAlternativeCaregivers(alternatives);

      if (patients.length > 0) {
        setDeleteReassignments(buildEmptyReassignments(patients));
        setShowDeleteReassignmentModal(true);
      } else {
        await performSystemCaregiverDelete(idToDelete, caregiverName);
      }
    } catch (error) {
      console.error('Failed to start system caregiver delete flow:', error);
      toast.error('Failed to check assigned patients.');
      setCaregiverToDelete(null);
    } finally {
      setIsDeleteProcessing(false);
    }
  };

  const handleSystemCaregiverReassignAndDelete = async () => {
    if (!caregiverToDelete) return;

    if (hasUnassignedReassignments(deleteAssignedPatients, deleteReassignments)) {
      toast.warning('Please select a new system caregiver for all patients.');
      return;
    }

    const idToDelete = caregiverToDelete.id;
    const caregiverName = getDisplayName(caregiverToDelete);
    setIsDeleteReassigning(true);
    try {
      await batchReassignPatientsToSystemCaregiver(idToDelete, deleteReassignments);
      await performSystemCaregiverDelete(idToDelete, caregiverName);
    } catch (error) {
      console.error('System caregiver reassignment before delete failed:', error);
      toast.error(formatDisplayError(error) || 'Failed to reassign patients.');
    } finally {
      setIsDeleteReassigning(false);
    }
  };

  const cancelSystemCaregiverDeleteReassignment = () => {
    setShowDeleteReassignmentModal(false);
    setCaregiverToDelete(null);
    setDeleteAssignedPatients([]);
    setDeleteAlternativeCaregivers([]);
    setDeleteReassignments({});
  };

  const confirmDelete = startSystemCaregiverDeleteFlow;

  const cancelDelete = () => {
    if (isDeleteProcessing || isDeleteReassigning) return;
    setShowDeleteModal(false);
    setCaregiverToDelete(null);
  };

  const handleAddSystemCaregiver = useCallback(() => {
    setCaregiverToEdit(null);
    setIsViewModal(false);
    setShowAddModal(true);
  }, []);

  const handleSort = useCallback(
    (key) => {
      const newOrder = sortBy === key && sortOrder === 'asc' ? 'desc' : 'asc';
      dispatch(setSorting({ sortBy: key, sortOrder: newOrder }));
      setCurrentPage(1);
    },
    [dispatch, sortBy, sortOrder]
  );

  const perPage = entriesPerPage === 'all' ? 9999 : parseInt(entriesPerPage, 10) || 10;
  const startIndex = ((pagination?.current_page || currentPage) - 1) * (pagination?.per_page || perPage);

  const columns = useMemo(
    () => {
      const useStaffLayout = Boolean(practiceId);
      return [
      staffCol({
        key: 'idx',
        title: '#',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (_, i) => <span>{startIndex + i + 1}</span>
      }, 'idx', useStaffLayout),
      staffCol({
        key: 'status',
        title: 'Status',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (row) => <StatusBadge status={scStatus(row.status)} />
      }, 'status', useStaffLayout),
      staffCol({
        key: 'name',
        title: 'Name',
        sortable: true,
        render: (row) => (
          <a
            href="#"
            className="scm-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewSystemCaregiver(row);
            }}
          >
            {getDisplayName(row)}
          </a>
        )
      }, 'name', useStaffLayout),
      staffCol({
        key: 'addr',
        title: 'Address',
        sortable: true,
        render: (row) => {
          const addr = formatAddressLines(row).join(', ');
          return (
            <div 
              style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} 
              title={addr}
            >
              {addr}
            </div>
          );
        }
      }, 'address', useStaffLayout),
      staffCol({ key: 'phone', title: 'Home Phone', sortable: true, render: (row) => formatPhoneForDisplay(row.phone, 'N/A') }, 'homePhone', useStaffLayout),
      staffCol({ key: 'cell', title: 'Cell Phone', sortable: true, render: (row) => formatPhoneForDisplay(row.cell_phone, 'N/A') }, 'cellPhone', useStaffLayout),
      staffCol({ key: 'fax', title: 'Fax', sortable: true, render: (row) => formatPhoneForDisplay(row.fax, 'N/A') }, 'fax', useStaffLayout),
      staffCol({ key: 'email', title: 'Email', sortable: true, render: (row) => getSafeValue(row.email) }, 'email', useStaffLayout),
      staffCol({
        key: 'actions',
        title: 'Action',
        className: 'reusable-table-centered-col',
        render: (row) => (
          <ActionButtons
            onView={() => handleViewSystemCaregiver(row)}
            onEdit={allowEdit ? () => handleEditSystemCaregiver(row) : null}
            onDelete={allowDelete ? () => handleDeleteSystemCaregiver(row) : null}
          />
        )
      }, 'action', useStaffLayout)
    ];
    },
    [allowEdit, allowDelete, startIndex, practiceId, handleViewSystemCaregiver, handleEditSystemCaregiver, handleDeleteSystemCaregiver]
  );

  const showLoading = listFetchLoading && !systemCaregiverFetchedKeys[cacheKey];
  const entriesSelectValue = entriesPerPage === 'all' ? 'all' : String(entriesPerPage);

  const toolbarEl = (
    <div className="scm-header-actions">
      <SearchBar
        value={localSearchTerm}
        onChange={setLocalSearchTerm}
        placeholder="Search caregivers..."
        label={null}
        className="scm-search-wrapper"
      />

      <select
        className="scm-status-select"
        value={localStatusFilter}
        onChange={(e) => {
          setLocalStatusFilter(e.target.value);
          setCurrentPage(1);
        }}
      >
        <option value="">All Status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>

      {user?.role_id === 1 && !practiceId && allowCreate && (
        <button className="scm-add-btn" onClick={handleAddSystemCaregiver}>
          <FontAwesomeIcon icon={faPlus} className="scm-add-icon" />
          Add System Caregivers
        </button>
      )}
    </div>
  );

  const tableCardEl = (
    <div className={practiceId ? STAFF_LIST_TABLE_CARD : 'practice-tab-table-card'}>
      <Table
        columns={columns}
        data={currentSystemCaregivers}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        isLoading={showLoading}
        loadingMode="skeleton"
        skeletonRows={10}
        emptyMessage="No system caregivers found"
      />

      <div className="scm-list-footer">
        <ListScreenPaginationBar
          entriesValue={entriesSelectValue}
          onEntriesChange={(value) => {
            setEntriesPerPage(value);
            setCurrentPage(1);
          }}
          leftSlot={
            practiceId && !hideEventLogsButton ? (
              <button
                type="button"
                className="scm-events-log-btn"
                onClick={() => {
                  if (typeof onOpenEventLogs === 'function') {
                    onOpenEventLogs();
                    return;
                  }
                  navigate(
                    appendFromDashboardQuery(`/event-logs/System%20Caregiver/all?practiceId=${practiceId}`, location)
                  );
                }}
              >
                <FontAwesomeIcon icon={faList} className="list-screen-me-2" />
                Event Logs
              </button>
            ) : null
          }
          currentPage={currentPage}
          totalRecords={pagination?.total}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );

  const modalsEl = (
    <>
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button type="button" className="modal-close" onClick={cancelDelete}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the system caregiver{' '}
                <strong>{getDisplayName(caregiverToDelete)}</strong>?
              </p>
              <p className="modal-warning">
                Assigned patients must be reassigned before this system caregiver can be deleted.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={cancelDelete}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-delete"
                onClick={confirmDelete}
                disabled={isDeleteProcessing}
              >
                {isDeleteProcessing ? 'Checking patients...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReassignmentModal
        show={showDeleteReassignmentModal}
        onClose={cancelSystemCaregiverDeleteReassignment}
        title="Reassign Patients"
        description={`Select a new system caregiver for each of the ${deleteAssignedPatients.length} patient(s) before deleting ${caregiverToDelete ? getDisplayName(caregiverToDelete) : 'this system caregiver'}.`}
        patients={deleteAssignedPatients}
        alternatives={deleteAlternativeCaregivers}
        reassignments={deleteReassignments}
        onReassignmentChange={(patientId, systemCaregiverId) =>
          setDeleteReassignments((prev) => ({ ...prev, [patientId]: systemCaregiverId }))
        }
        onAssignAll={(systemCaregiverId) =>
          setDeleteReassignments(buildReassignmentsForAll(deleteAssignedPatients, systemCaregiverId))
        }
        onSave={handleSystemCaregiverReassignAndDelete}
        isSaving={isDeleteReassigning}
        saveLabel="Reassign & Delete"
        optionPlaceholder="Select system caregiver..."
        reassignToLabel="Reassign to system caregiver"
        bulkAssignLabel="Assign all to"
        getPatientName={getSystemCaregiverPatientName}
        getCurrentAssignee={() => (caregiverToDelete ? getDisplayName(caregiverToDelete) : 'N/A')}
        getAlternativeName={(item) =>
          `${item?.last_name || ''}, ${item?.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A'
        }
      />

      <AddSystemCaregiverModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setCaregiverToEdit(null);
          setIsViewModal(false);
        }}
        onSave={() => {
          setShowAddModal(false);
          setCaregiverToEdit(null);
          setIsViewModal(false);
          dispatch(fetchSystemCaregivers({ ...listParams, force: true }));
        }}
        isViewMode={isViewModal}
        isEditMode={!!caregiverToEdit && !isViewModal}
        editId={caregiverToEdit?.id}
        practiceId={practiceId}
      />

    </>
  );

  if (practiceId) {
    return (
      <div className="scm-container">
        {error && !showLoading && (
          <div className="alert alert-danger mb-3" role="alert">
            {formatDisplayError(error)}
          </div>
        )}

        <div className="scm-header-row">
          {toolbarEl}
        </div>

        {tableCardEl}
        {modalsEl}
      </div>
    );
  }

  return (
    <div className="list-screen-page-container scm-page">
      <div className="scm-page-header">
        <h2 className="dashboard-title">System Caregiver List</h2>
      </div>

      {error && !showLoading && (
        <div className="alert alert-danger mb-3" role="alert">
          {formatDisplayError(error)}
        </div>
      )}

      <div className="scm-list-frame">
        <div className="scm-list-panel">
          <div className="scm-list-body">
            <div className="scm-header-row">
              {toolbarEl}
            </div>
            {tableCardEl}
          </div>
        </div>
      </div>

      {!hideEventLogsButton && (
        <div className="scm-event-log-actions">
          <button
            type="button"
            className="scm-events-log-btn"
            onClick={() => {
              if (typeof onOpenEventLogs === 'function') {
                onOpenEventLogs();
                return;
              }
              setShowLogSection(true);
            }}
          >
            <FontAwesomeIcon icon={faList} className="list-screen-me-2" />
            Event Logs
          </button>
        </div>
      )}

      {showLogSection && (
        <div ref={logOverlayRef} className="practices-list-modal-overlay">
          <div
            ref={logContainerRef}
            className="practices-list-modal-container"
            style={{ maxWidth: '900px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">System Caregiver Event Logs</h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={() => setShowLogSection(false)}
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <EventLogSection moduleId="System Caregiver" targetUserId="all" />
            </div>
          </div>
        </div>
      )}

      {modalsEl}
    </div>
  );
};

export default SystemCaregiverManagement;

const DetailRow = ({ label, value, multiline = false }) => (
  <div className="scm-detail-row">
    <span className="scm-detail-label">{label}</span>
    <span className="scm-detail-separator">:</span>
    <span className={`scm-detail-value ${multiline ? 'scm-detail-value--multiline' : ''}`}>
      {Array.isArray(value) ? (
        value.map((line, index) => (
          <Fragment key={`${label}-${index}`}>
            {line || 'N/A'}
            {index < value.length - 1 && <br />}
          </Fragment>
        ))
      ) : (
        value || 'N/A'
      )}
    </span>
  </div>
);
